import {SpanIcon} from '@mazraeapp/react_icon_kit';
import {useCallback, useEffect, useMemo, useState} from 'react';
import Stories from 'react-insta-stories';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import shopLogo from '../../../../assets/image/newShop/defaultImg.png';
import DotsLoading from '../../../../components/dotsLoading';
import {
  useGetPostStories,
  useGetSupplierStories,
} from '../../../../components/mazraeKit/story/hooks';
import {useAuth} from '../../../../context/authContext';
import useDeleteStory from '../../../../services/hookService/deleteStory/useDeleteStory';
import {usePostStoryClick} from '../../../../services/hookService/postStroyClick/usePostStoryClick';
import {timeAgoPersian} from '../../../../utilities/helper/GetPersianDateTime';
import DeleteStoryBS from '../DeleteStoryBS';
import './style.css';

// Static Styles
// This prevents unnecessary re-renders inside the react-insta-stories library
const STORY_STYLES = {
  width: '100vw',
  height: '100%',
  position: 'center',
  background: 'blue',
};
const PROGRESS_CONTAINER_STYLES = {
  flexDirection: 'row-reverse',
};
const PROGRESS_WRAPPER_STYLES = {
  rotate: '180deg',
};

const StorySection = ({
  isMain,
  endHandler,
}: {
  isMain: boolean;
  endHandler?: () => void;
}) => {
  const navigate = useNavigate();
  const [openStories, setOpenStories] = useState(false);
  const [openedStoryId, setOpenedStoryId] = useState('');
  const [background, setBackground] = useState('gray');
  const [deletedGuid, setDeletedGuid] = useState<string | null>(null);
  const [showDeleteStroyBS, setShowDeleteStroyBS] = useState(false);

  const {stories: storesWithStory, storiesIsLoading} = useGetSupplierStories();
  const {supplierProfile} = useAuth();

  const handleStoryClick = useCallback((guid: string) => {
    setOpenedStoryId(guid);
    setOpenStories(true);
  }, []);

  const {postsStory} = useGetPostStories(openedStoryId);
  const {mutate, isLoading} = useDeleteStory();
  const {mutate: stroryClickHandler} = usePostStoryClick();

  const readyStoryForDelete = useCallback(
    (guid: string) => {
      if (isLoading) return;
      setDeletedGuid(guid);
      setShowDeleteStroyBS(true);
    },
    [isLoading],
  );

  const handleDeleteStory = useCallback(
    (guid: string) => {
      mutate(guid, {
        onSuccess: () => {
          toast.success('استوری شما با موفقیت حذف شد');
          setOpenedStoryId('');
          setOpenStories(false);
          setShowDeleteStroyBS(false);
        },
      });
    },
    [mutate],
  );

  const storyContent = useMemo(() => {
    if (!postsStory) return [];

    return postsStory.map(story => ({
      url: story.image,
      type: 'image', // 3. Explicitly tell the library this is an image so it preloads better

      content: () => (
        <div className="relative flex items-center justify-center w-full h-full">
          <div className="absolute top-0 z-10 w-full h-32 bg-gradient-to-b from-gray-700 to-transparent mix-blend-multiply" />

          {/* Story image */}
          <img
            src={story.image}
            alt="storyImage"
            decoding="sync"
            className="object-contain w-full h-screen"
          />

          {/* Store logo and info */}
          <div className="absolute top-6 right-5 text-right gap-1 items-start justify-between z-[1000]">
            <div className="flex flex-row-reverse items-center justify-center gap-1">
              <img
                src={story.store_logo ?? shopLogo}
                alt="storeLogo"
                className="object-cover border-2 rounded-full cursor-pointer size-12"
                onClick={() => navigate(story.link)}
              />
              <div className="flex flex-col items-end justify-center gap-1 text-right">
                <span className="font-bold text-white text-md">
                  {story.store_title}
                </span>
                <span className="text-xs text-white" dir="rtl">
                  {timeAgoPersian(story.created_at)}
                </span>
              </div>
            </div>
          </div>

          {/* Close button */}
          <button
            className="absolute top-5 left-3 z-[10000] p-2"
            onClick={() => {
              setOpenStories(false);
              setOpenedStoryId('');
              endHandler?.();
            }}
          >
            <SpanIcon className="text-3xl text-white icon-nameClose-remove" />
          </button>
          {/* navigation to store */}
          {story.link && (
            <button
              className={`absolute left-1/2 -translate-x-1/2 z-[10000] bg-white text-farm-shop-500 flex items-center justify-center gap-1 rounded-lg h-12 px-4 text-md text-center ${
                story.is_story_owner ? 'bottom-[60px]' : 'bottom-[20px]'
              }`}
              onClick={() => {
                stroryClickHandler(story.guid);
                if (story?.is_external_url) {
                  const url = story.link.startsWith('http')
                    ? story.link
                    : `https://${story.link}`;

                  window.open(url, '_blank', 'noopener,noreferrer');
                } else {
                  navigate(story.link);
                }
              }}
            >
              <SpanIcon className="text-2xl icon-nameArrow---Left" />

              <span
                dir="rtl"
                className="block w-32 overflow-hidden text-ellipsis whitespace-nowrap"
              >
                {story.link_description}
              </span>
            </button>
          )}
          {/* Bottom bar */}
          {story.is_story_owner && (
            <div className="absolute bottom-0 w-full z-[10000]">
              <div className="flex items-center justify-between w-full p-3 text-white bg-slate-800/70">
                <button
                  onClick={() => readyStoryForDelete(story.guid)}
                  className="flex items-center gap-1 text-white text-md"
                >
                  <span>حذف</span>
                  <SpanIcon className="text-xl icon-nameDelete" />
                </button>

                <button className="flex items-center gap-1 text-white text-md">
                  <span>آمار کلیک : {story.total_click_count} تا</span>
                  <SpanIcon className="text-xl icon-nameclick" />
                </button>

                <button className="flex items-center gap-1 text-white text-md">
                  <span>بازدید : {story.total_view_count} نفر</span>
                  <SpanIcon className="text-xl icon-nameShow" />
                </button>
              </div>
            </div>
          )}
        </div>
      ),
    }));
  }, [
    postsStory,
    navigate,
    readyStoryForDelete,
    stroryClickHandler,
    endHandler,
  ]);

  const onStoryStart = useCallback(
    (index: number) => {
      if (!postsStory) return;
      const currentStory = postsStory[index];

      // requestAnimationFrame ensures the browser finishes drawing the image
      // BEFORE React forces a parent re-render to update the background color
      requestAnimationFrame(() => {
        setBackground(currentStory?.preview_color || 'gray');
      });
    },
    [postsStory],
  );

  const onStoryEnd = useCallback(
    (index: number) => {
      if (index + 1 === postsStory?.length && !isMain) {
        endHandler && endHandler();
      }
    },
    [postsStory, isMain, endHandler],
  );

  useEffect(() => {
    if (!isMain) {
      const mainStory = storesWithStory?.find(
        store => store.store.guid === supplierProfile?.store?.guid,
      );
      handleStoryClick(mainStory?.guid || '');
    }
  }, [isMain, storesWithStory, supplierProfile, handleStoryClick]);

  const handleNextStory = useCallback(() => {
    if (!storesWithStory || !isMain) return;
    const mainStoryIndex = storesWithStory?.findIndex(
      story => story.guid === openedStoryId,
    );
    if (mainStoryIndex + 1 === storesWithStory.length) {
      setOpenStories(false);
      setOpenedStoryId('');
    } else {
      const nextStory = storesWithStory[mainStoryIndex + 1];
      setOpenedStoryId(nextStory?.guid || '');
    }
  }, [storesWithStory, isMain, openedStoryId]);

  // 5. Memoize the container styles so it only creates a new object when background actually changes
  const storyContainerStyles = useMemo(
    () => ({
      background,
    }),
    [background],
  );

  return (
    <div>
      {isMain && (
        <div className="min-h-[155px] bg-farm-shop-500 p-4">
          {/* STORY circle thumbnail =============================== */}
          <div className="flex items-center justify-center mx-auto ">
            {storiesIsLoading ? (
              <div className="pt-10">
                <DotsLoading />
              </div>
            ) : (
              <div className="flex gap-3 overflow-y-auto max-w-[100vw] ">
                {storesWithStory &&
                  storesWithStory.map(store => (
                    <div key={store.guid}>
                      <div
                        onClick={() => handleStoryClick(store?.guid)}
                        className="flex flex-col items-center justify-center gap-1 cursor-pointer max-w-20"
                      >
                        <div className="bg-gradient-to-br from-[#FFFFFF] to-[#84FF00] rounded-full p-[0.2rem]">
                          <div className="size-16 rounded-full overflow-hidden border-[4px] border-[#74B82D]">
                            <img
                              src={store?.store?.logo ?? shopLogo}
                              alt="logo"
                              className="object-cover w-full h-full"
                            />
                          </div>
                        </div>
                        <p className="text-sm text-center text-white">
                          {store?.store?.title}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* STORY Modal ============================================ */}
      {openStories && storyContent?.length > 0 && (
        <div className="slider-sina" style={{direction: 'ltr'}}>
          <Stories
            key={openedStoryId}
            stories={storyContent}
            defaultInterval={5000}
            width="100vw"
            height="100dvh"
            preloadCount={5}
            onAllStoriesEnd={handleNextStory}
            onStoryStart={onStoryStart}
            onStoryEnd={onStoryEnd}
            storyStyles={STORY_STYLES}
            storyContainerStyles={storyContainerStyles}
            progressContainerStyles={PROGRESS_CONTAINER_STYLES}
            progressWrapperStyles={PROGRESS_WRAPPER_STYLES}
          />
        </div>
      )}
      <DeleteStoryBS
        open={showDeleteStroyBS}
        setIsOpen={() => setShowDeleteStroyBS(false)}
        guid={deletedGuid}
        handler={handleDeleteStory}
      />
    </div>
  );
};

export default StorySection;
